import React, { useEffect, useState } from 'react'
import deepEqual from 'lodash/isEqual'
import { Colors, Shapes } from 'src/components/theme'
import { MapStyleConfig } from 'src/components/map-new/types'
import { useIsMapEditor } from 'src/components/map-editor/contexts/editor'
import { Typography } from '@mui/material'
import styled from '@emotion/styled'
import { Flex } from 'src/components/flex-containers'
import { AntSwitch } from 'src/components/switch'
import { Map } from '@emotion-icons/fa-solid/Map'
import { Popover } from '@mui/material'
import { MAP_DEFAULT_STYLE_CONFIG, MAP_DEFAULT_STYLE_NAMES } from 'src/components/map/mapbox/config'

const MAP_CONTROLS_BORDER_RADIUS = 7

const LAYER_OPTION_CLASSNAME = 'layer-option-name'

type MapComponentStyleConfigControlProps = {
  mapStyleConfig: MapStyleConfig
  onMapStyleConfigChange: (mapStyleConfig: MapStyleConfig) => void
  className?: string
}

const Option = styled(Flex)`
  flex-direction: row nowrap;
  align-items: center;
  justify-content: flex-start;
  padding: ${Shapes.spacing[1]}px;

  & .${LAYER_OPTION_CLASSNAME} {
    padding-left: ${Shapes.spacing[4]}px;
  }
`

const MapLayerOptions = styled(Flex)`
  background-color: ${Colors.bgWhite};
  padding: ${Shapes.spacing[2]}px;
  border: 1px solid ${Colors.blueDark};
  border-radius: ${MAP_CONTROLS_BORDER_RADIUS}px;
  .MuiPaper-root {
    border-radius: ${MAP_CONTROLS_BORDER_RADIUS}px;
  }
`

const LayerMenuButton = styled(Flex)`
  width: 104px;
  gap: ${Shapes.spacing[1]}px;
  background-color: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  padding: ${Shapes.spacing[2]}px;
  border-radius: ${Shapes.borderRadius}px;
  justify-content: center;
  cursor: pointer;
`

const LayerConfigSection = styled(Flex)`
  gap: ${Shapes.spacing[1]}px;
  align-items: flex-end;
`

export const MapComponentStyleConfigControl = (props: MapComponentStyleConfigControlProps) => {
  const isEditor = useIsMapEditor()
  const { mapStyleConfig, onMapStyleConfigChange } = props
  const [internalMapStyleConfig, setInternalMapStyleConfig] = useState({
    ...MAP_DEFAULT_STYLE_CONFIG,
    ...mapStyleConfig,
  })

  const anchorEl = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLDivElement> | undefined) => {
    setOpen(!open)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const onChange = (key: keyof MapStyleConfig) => () => {
    const newMapStyleConfig = { ...MAP_DEFAULT_STYLE_CONFIG, ...mapStyleConfig }
    newMapStyleConfig[key] = !newMapStyleConfig[key]
    setInternalMapStyleConfig(newMapStyleConfig)
    onMapStyleConfigChange(newMapStyleConfig)
  }

  useEffect(() => {
    if (!deepEqual(mapStyleConfig, internalMapStyleConfig)) {
      setInternalMapStyleConfig({ ...MAP_DEFAULT_STYLE_CONFIG, ...mapStyleConfig })
    }
  }, [mapStyleConfig])

  if (!isEditor) {
    return null
  }

  return (
    <LayerConfigSection col>
      <Popover
        id="map-layers-popover"
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleClose}
        marginThreshold={Shapes.spacing[2]}
        style={{ marginTop: -Shapes.spacing[2] }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}>
        <MapLayerOptions>
          <div>
            {Object.keys(internalMapStyleConfig).map((key) => {
              const typedKey = key as keyof MapStyleConfig
              return (
                <Option key={key}>
                  <AntSwitch
                    edge="end"
                    color="primary"
                    onChange={onChange(typedKey)}
                    checked={internalMapStyleConfig[typedKey]}
                  />
                  <Typography
                    className={LAYER_OPTION_CLASSNAME}
                    variant="text1"
                    color={Colors.textDark}
                    style={{ whiteSpace: 'nowrap' }}>
                    {MAP_DEFAULT_STYLE_NAMES[typedKey]}
                  </Typography>
                </Option>
              )
            })}
          </div>
        </MapLayerOptions>
      </Popover>
      <LayerMenuButton
        ref={anchorEl}
        onClick={handleClick}
        style={{ borderColor: open ? Colors.blueDark : Colors.border }}>
        <Map size={16} color={Colors.blueDark} />
        <Typography variant="text1" color={Colors.textMedium}>
          Map Layers
        </Typography>
      </LayerMenuButton>
    </LayerConfigSection>
  )
}
